/**
 * Communication Center data layer.
 *
 * There is no dedicated "communication" backend — this reads the same
 * Command Center task feed (`/api/momentumkanban/*`) that already carries a
 * `source` field per task (0 Manual, 1 Email, 2 Voice, 3 AI Agent, 4 Other),
 * filtered down to Email + Voice, and rides the same SignalR hub Command
 * Center uses for live updates. It is real data, not a parallel inbox.
 *
 * The list endpoint has no `source` query param, so filtering happens
 * client-side after a single broad fetch. That means a page can look sparse
 * if most current tasks are Manual/AI-Agent — swap in a real `source`
 * filter param here the moment the backend offers one; nothing downstream
 * needs to change.
 */
import * as signalR from '@microsoft/signalr';
import { kanbanTsToIso } from '../utils/dateTime';

const KANBAN_STATUS_IDS: Record<string, CommStatus> = {
  '11111111-1111-1111-1111-111111111111': 'todo',
  '22222222-2222-2222-2222-222222222222': 'inprogress',
  '33333333-3333-3333-3333-333333333333': 'inreview',
  '44444444-4444-4444-4444-444444444444': 'done',
};
const ALL_STATUS_IDS = Object.keys(KANBAN_STATUS_IDS);

export type CommStatus = 'todo' | 'inprogress' | 'inreview' | 'done';
export type CommSource = 'email' | 'voice';

export interface CommThreadComment {
  role: string;
  message: string;
  created?: string;
}

export interface CommItem {
  taskId: string;
  taskCode: string;
  source: CommSource;
  title: string;
  description: string;
  status: CommStatus;
  createdAt?: string;
  updatedAt?: string;
  lastMessage?: CommThreadComment;
  commentCount: number;
  linkedInsuredName?: string;
  linkedPolicyNumber?: string;
  agentName?: string;
  transactionId?: string;
}

function authHeaders(): HeadersInit {
  return { Authorization: `Bearer ${localStorage.getItem('authToken') || ''}` };
}

function readTimestamp(row: any, kind: 'created' | 'updated'): string | undefined {
  const raw = kind === 'updated'
    ? (row?.updatedAt ?? row?.updated_at ?? row?.modifiedAt ?? row?.modified_at)
    : (row?.createdAt ?? row?.created_at ?? row?.createdDate ?? row?.created_date);
  if (typeof raw === 'string' && raw.trim()) return kanbanTsToIso(raw) ?? raw;
  if (typeof raw === 'number' && Number.isFinite(raw)) return new Date(raw).toISOString();
  return undefined;
}

function mapTaskToCommItem(t: any, i: number): CommItem {
  return {
    taskId: String(t.id ?? i),
    taskCode: t.taskNumber || `CMD-${i + 1}`,
    source: t.source === 2 ? 'voice' : 'email',
    title: t.title || '(no subject)',
    description: t.description || '',
    status: KANBAN_STATUS_IDS[t.statusId] ?? 'todo',
    createdAt: readTimestamp(t, 'created'),
    updatedAt: readTimestamp(t, 'updated'),
    lastMessage: t.lastCommentData
      ? { role: t.lastCommentData.role ?? 'AI', message: t.lastCommentData.message ?? '', created: readTimestamp(t.lastCommentData, 'created') }
      : undefined,
    commentCount: t.commentCount ?? t.commentsData?.length ?? 0,
    linkedInsuredName: t.linkedInsuredName ?? t.linked_insured_name ?? undefined,
    linkedPolicyNumber: t.linkedPolicyNumber ?? t.linked_policy_number ?? undefined,
    agentName: t.agentName ?? t.agent_name ?? undefined,
    transactionId: typeof t.transactionId === 'string' && t.transactionId ? t.transactionId : undefined,
  };
}

/** Every Email/Voice-sourced task, across a single broad page of the real task feed. */
export async function fetchCommunicationItems(pageSize = 200): Promise<CommItem[]> {
  const params = new URLSearchParams({ page: '1', pageSize: String(pageSize) });
  ALL_STATUS_IDS.forEach(id => params.append('statusIds', id));
  const res = await fetch(`${(process.env.REACT_APP_Auth_Url || '')}/api/momentumkanban/tasks?${params}`, { headers: authHeaders() });
  if (!res.ok) throw new Error(`fetch communication items failed: ${res.status}`);
  const json = await res.json();
  const list: any[] = Array.isArray(json) ? json : (json?.data ?? []);
  return list
    .filter(t => t.source === 1 || t.source === 2)
    .map(mapTaskToCommItem);
}

export interface CommPage {
  /** Email/Voice items on this page of the task feed. */
  items: CommItem[];
  /** Whether the task feed has more pages after this one. */
  hasMore: boolean;
}

/**
 * One page of the task feed, kept to Email/Voice. The page and total are the
 * feed's (all sources), since the list endpoint can't filter by source — a
 * page can hold fewer than `pageSize` items, or none.
 */
export async function fetchCommunicationPage(page: number, pageSize = 50, search = ''): Promise<CommPage> {
  const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
  if (search.trim()) params.set('search', search.trim());
  ALL_STATUS_IDS.forEach(id => params.append('statusIds', id));
  const res = await fetch(`${(process.env.REACT_APP_Auth_Url || '')}/api/momentumkanban/tasks?${params}`, { headers: authHeaders() });
  if (!res.ok) throw new Error(`fetch communication items failed: ${res.status}`);
  const json = await res.json();
  const list: any[] = Array.isArray(json) ? json : (json?.data ?? []);
  const total: number = json?.totalCount ?? list.length;
  return {
    items: list.filter(t => t.source === 1 || t.source === 2).map(mapTaskToCommItem),
    hasMore: page * pageSize < total,
  };
}

/** Full detail for one item, including its comment thread. */
export async function fetchCommunicationItemDetail(taskId: string): Promise<{ item: CommItem; comments: CommThreadComment[] }> {
  const res = await fetch(`${(process.env.REACT_APP_Auth_Url || '')}/api/momentumkanban/task/${taskId}`, { headers: authHeaders() });
  if (!res.ok) throw new Error(`fetch communication item failed: ${res.status}`);
  const json = await res.json();
  const t = json?.data ?? json;
  const rawComments: any[] = Array.isArray(t?.commentsData) ? t.commentsData : Array.isArray(t?.comments) ? t.comments : [];
  const comments: CommThreadComment[] = rawComments.map((c: any) => ({
    role: c.role ?? c.author ?? 'AI',
    message: c.message ?? c.text ?? '',
    created: readTimestamp(c, 'created'),
  }));
  return { item: mapTaskToCommItem(t, 0), comments };
}

/**
 * Live updates — the exact same hub/event Command Center subscribes to
 * (`KanbanTaskChanged` on `/hubs/kanban`), since these are the same tasks.
 */
export function subscribeToCommunicationUpdates(onChange: () => void): () => void {
  const conn = new signalR.HubConnectionBuilder()
    .withUrl(`${(process.env.REACT_APP_Auth_Url || '')}/hubs/kanban`, { accessTokenFactory: () => localStorage.getItem('authToken') || '' })
    .withAutomaticReconnect()
    .configureLogging(signalR.LogLevel.Warning)
    .build();

  conn.on('KanbanTaskChanged', onChange);
  conn.onreconnected(() => {
    onChange();
    const userId = localStorage.getItem('userId');
    if (userId) conn.invoke('JoinUserGroup', userId).catch(() => {});
  });

  conn.start()
    .then(() => {
      const userId = localStorage.getItem('userId');
      if (userId) conn.invoke('JoinUserGroup', userId).catch(() => {});
    })
    .catch(() => {});

  return () => { conn.stop().catch(() => {}); };
}

// Same hub/event as subscribeToCommunicationUpdates above — generic alias for
// callers that aren't communication-specific (e.g. SidebarDigitalTeam.tsx,
// which uses it to keep agent workload counts live).
export const subscribeToKanbanUpdates = subscribeToCommunicationUpdates;
